import { head, includes, negate } from 'lodash';
import type { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import type { FilePickerController } from '@milanote/roma';
import type { GenericReduxDispatch } from '../../../common/actions/actionTypes';
import type { ImMNElement, ImMNElementMap, MNElement } from '../../../common/elements/elementModelTypes';
import { COLUMN_DEFAULT_HEIGHT_IN_GRID_UNIT } from '../../../common/columns/columnConstants';
import { ElementType } from '../../../common/elements/elementTypes';
import { BrowserEngineType } from '../../../common/platform/platformTypes';
import { ELEMENT_DEFAULT_WIDTH, ELEMENT_MOVE_OPERATIONS } from '../../../common/elements/elementConstants';
import {
    getCanvasOriginCoordinates,
    getElementId,
    getElementType,
    getFileUrl,
    getImageProp,
    getLinkUrl,
    getLocationPosition,
    getShowFullAspectRatio,
    isElementLocked,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getChildren, getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getHighestScore, getLowestScore } from '../../../common/elements/utils/elementLocationUtils';
import { MINIMUM_CANVAS_ORIGIN } from '../../../common/elements/utils/elementPositionUtils';
import { sortBy2dPosition, sortByScoreProperty } from '../../../common/elements/utils/elementSortUtils';
import {
    isBoard,
    isColumn,
    isDocument,
    isFile,
    isLine,
    isTaskList,
} from '../../../common/elements/utils/elementTypeUtils';
import { asObject, prop } from '../../../common/utils/immutableHelper';
import randomString from '../../../common/uid/randomString';
import { getBoundingRectInGridUnits } from '../../canvas/dnd/canvasUtil';
import { useMeasurementsDispatch } from '../../components/measurementsStore/MeasurementsProvider';
import { getMeasurementsMapThunk } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import type { ImElementMeasurementMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { triggerDownload } from '../../element/attachments/attachmentDownloadActions';
import {
    createAliasToElement,
    setElementTypeAndUpdateElement,
    updateMultipleElements,
} from '../../element/actions/elementActions';
import {
    cloneSelectedElement,
    duplicateSelectedElements,
    moveElementsToTrash,
} from '../../element/actions/elementShortcutActions';
import { moveMultipleElements } from '../../element/actions/elementMoveActions';
import { uploadImage } from '../../element/attachments/attachmentActions';
import { convertColumnToBoard, createColumnWithChildren } from '../../element/column/columnActions';
import { convertElementsToDocument } from '../../element/document/documentActions';
import { convertToSketch } from '../../element/drawing/annotation/store/annotationActions';
import { isVideoFile } from '../../element/file/utils/fileGetMediaPlayerType';
import { getImageSource } from '../../element/image/imageHelper';
import { deselectAllElements, startEditingElement } from '../../element/selection/selectionActions';
import { getMainEditorId, getMainEditorKey } from '../../element/utils/elementEditorUtils';
import {
    convertBoardToTemplate,
    revertTemplateToBoard,
} from '../../workspace/boardTemplatePickerPopup/boardTemplateActions';
import { downloadAsSvg } from '../../element/drawing/utils/drawingUtils';
import { connectElementsWithLines } from '../../workspace/toolbar/components/selectionTools/connectTool/connectionActions';
import { getIsFeatureEnabledForCurrentUser } from '../../element/feature/elementFeatureSelector';
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';
import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getElements } from '../../element/selectors/elementsSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { navigateToSelectedBoardInNewTab } from '../../reducers/navigationActions';
import getGridSize from '../../utils/grid/gridSizeSelector';
import platformSingleton from '../../platform/platformSingleton';
import { isPlatformModernMobileApp } from '../../platform/utils/platformDetailsUtils';
import { writeToNativeClipboard } from '../../capacitor/utils/capacitorClipboardUtils';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { ClipboardOperation } from '../../workspace/shortcuts/clipboard/clipboardConstants';
import { DEFAULT_INCREMENT } from '../../../common/elements/listOrderScores/scoreCalculator';
import {
    pasteElementStyles,
    pasteElements,
    saveToClipboard,
} from '../../workspace/shortcuts/clipboard/clipboardActions';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import type { UnknownAction } from 'redux';
import type { ElementContextMenuActions } from './context/elementContextMenuContext';

type UseElementContextMenuActionsArgs = {
    isEditable: boolean;
    filePicker?: FilePickerController;
};

const renderSecureImagesThroughCFSelector = getIsFeatureEnabledForCurrentUser(
    ExperimentId.renderSecureImagesThroughCF,
    true,
);

const clipboardWritePermission: PermissionDescriptor = { name: 'clipboard-write' as PermissionName };

/** Binds element-menu actions to the selected elements and active board. */
export const useElementContextMenuActions = ({
    isEditable,
    filePicker,
}: UseElementContextMenuActionsArgs): ElementContextMenuActions => {
    const dispatch = useDispatch<GenericReduxDispatch>();
    const elements = useSelector<unknown, ImMNElementMap>(getElements);
    const selectedElements = useSelector<unknown, List<ImMNElement>>(getSelectedElements);
    const currentBoardId = useSelector(getCurrentBoardId);
    const currentlyEditingId = useSelector(getCurrentlyEditingId);
    const gridSize = useSelector(getGridSize);
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const renderSecureImagesThroughCF = useSelector(renderSecureImagesThroughCFSelector);
    const measurementsDispatch = useMeasurementsDispatch();
    const measurements = measurementsDispatch(
        getMeasurementsMapThunk() as unknown as UnknownAction,
    ) as unknown as ImElementMeasurementMap;

    /** Falls back to the app clipboard when native copy and cut are unavailable. */
    const saveSelectedElementsToClipboard = ({
        operation,
        event,
    }: {
        operation: ClipboardOperation;
        event?: Event;
    }) => {
        const success = document.execCommand(operation);
        if (success || !selectedElements.size || currentlyEditingId) return;

        event?.preventDefault();
        const elementsToSave = selectedElements.toJS() as MNElement[];
        dispatch(
            saveToClipboard({
                operation,
                elements: elementsToSave,
                saveId: randomString(16),
                pasteCount: operation === ClipboardOperation.cut ? -1 : 0,
                elementsBoundingRect: getBoundingRectInGridUnits({
                    elements: elementsToSave,
                    measurements,
                    gridSize,
                }),
                isEditable,
            } as unknown as Parameters<typeof saveToClipboard>[0]),
        );
    };

    /** Uploads a replacement file for every selected media element. */
    const uploadReplacementImage = (file: File) => {
        const transactionId = getNewTransactionId();
        selectedElements.forEach((element) => {
            if (!element) return;

            dispatch(
                uploadImage({
                    id: getElementId(element),
                    file,
                    imageType: element.get('elementType'),
                    transactionId,
                    options: { onlyReplaceImage: isVideoFile(element) },
                } as Parameters<typeof uploadImage>[0]),
            );
        });
    };

    /** Downloads the original asset for every selected file or image. */
    const downloadOriginal = () => {
        selectedElements.forEach((element) => {
            const id = getElementId(element);
            const url = isFile(element)
                ? getFileUrl(element)
                : getImageSource({
                      imageSize: 'original',
                      imageDetails: getImageProp(element),
                      elementId: id,
                      useSecureMediaUrl: true,
                      renderSecureImagesThroughCF,
                      optimizeForWebRender: false,
                  });
            if (url) dispatch(triggerDownload({ url, id } as Parameters<typeof triggerDownload>[0]));
        });
    };

    /** Groups the selected non-column, non-line elements into a new column. */
    const groupIntoColumn = () => {
        const filteredElements = selectedElements.filter(negate(isLine)).filter(negate(isColumn)).toJS() as MNElement[];
        const sortedSelectedElements = filteredElements.sort(sortBy2dPosition);
        const childIdArray = sortedSelectedElements.map(getElementId);
        const elementsBoundingRect = getBoundingRectInGridUnits({
            elements: sortedSelectedElements,
            measurements,
            gridSize,
        });
        const columnHeight = sortedSelectedElements.reduce(
            (height, element) => height + measurements.getIn([element.id, 'height']) / gridSize,
            COLUMN_DEFAULT_HEIGHT_IN_GRID_UNIT,
        );
        const currentBoard = getElement(elements, currentBoardId);
        const canvasOrigin = getCanvasOriginCoordinates(currentBoard);
        const firstElement = head(sortedSelectedElements);
        if (!firstElement) return;

        const location = {
            ...firstElement.location,
            position: {
                ...getLocationPosition(firstElement),
                x: Math.max(
                    MINIMUM_CANVAS_ORIGIN,
                    (elementsBoundingRect.right + elementsBoundingRect.left) / 2 -
                        ELEMENT_DEFAULT_WIDTH / 2 +
                        prop('x', canvasOrigin),
                ),
                y: Math.max(
                    MINIMUM_CANVAS_ORIGIN,
                    (elementsBoundingRect.bottom + elementsBoundingRect.top) / 2 -
                        columnHeight / 2 +
                        prop('y', canvasOrigin),
                ),
            },
        };
        const linesIdsToBeDeleted = elements
            .filter((element) => {
                if (!element || !isLine(element)) return false;

                const lineElement = asObject<MNElement>(element);
                const startElementId = lineElement.content?.start?.elementId;
                const endElementId = lineElement.content?.end?.elementId;
                return includes(childIdArray, startElementId) || includes(childIdArray, endElementId);
            })
            .map(getElementId)
            .toArray();
        const transactionId = getNewTransactionId();

        dispatch(
            createColumnWithChildren({
                location,
                childIdArray,
                onDrop: false,
                transactionId,
            } as Parameters<typeof createColumnWithChildren>[0]),
        );
        dispatch(moveElementsToTrash({ currentBoardId, elementIds: linesIdsToBeDeleted, transactionId }));
        dispatch(deselectAllElements());
    };

    /** Changes the selected elements' canvas stacking order. */
    const changeStackingOrder = ({ bringToFront }: { bringToFront: boolean }) => {
        const currentBoardElements = getChildren(elements, currentBoardId).valueSeq().toJS() as MNElement[];
        const sortedSelectedElements = selectedElements.sort(sortByScoreProperty).toJS() as MNElement[];
        const selectedElementIds = sortedSelectedElements.map(getElementId);
        const unselectedElements = currentBoardElements.filter(
            (element) => !selectedElementIds.includes(getElementId(element)),
        );
        const boundaryScore = bringToFront ? getHighestScore(unselectedElements) : getLowestScore(unselectedElements);
        const moves = sortedSelectedElements.map((element, index) => {
            const location = element.location as NonNullable<MNElement['location']>;
            const position = location.position as NonNullable<typeof location.position>;

            return {
                id: element.id,
                location: {
                    ...location,
                    position: {
                        ...position,
                        score: bringToFront
                            ? boundaryScore + (index + 1) * DEFAULT_INCREMENT
                            : boundaryScore - (sortedSelectedElements.length - index) * DEFAULT_INCREMENT,
                    },
                },
            };
        });

        dispatch(
            moveMultipleElements({
                moves,
                moveOperation: ELEMENT_MOVE_OPERATIONS.CHANGE_ORDER,
            } as Parameters<typeof moveMultipleElements>[0]) as unknown as UnknownAction,
        );
    };

    return {
        openInNewTab: () => dispatch(navigateToSelectedBoardInNewTab()),
        paste: () => dispatch(pasteElements({} as Parameters<typeof pasteElements>[0])),
        cut: () => saveSelectedElementsToClipboard({ operation: ClipboardOperation.cut }),
        copy: () => saveSelectedElementsToClipboard({ operation: ClipboardOperation.copy }),
        pasteStyles: () => dispatch(pasteElementStyles()),
        duplicate: () => dispatch(duplicateSelectedElements({ shouldConvertAliasToBoard: false })),
        duplicateAliasToBoard: () => dispatch(duplicateSelectedElements({ shouldConvertAliasToBoard: true })),
        clone: () => dispatch(cloneSelectedElement()),
        moveToTrash: () =>
            dispatch(
                moveElementsToTrash({
                    currentBoardId,
                    elementIds: selectedElements.map(getElementId).toJS(),
                }),
            ),
        rename: () => {
            const element = selectedElements.first();
            const elementType = getElementType(element);
            dispatch(
                startEditingElement({
                    id: getElementId(element),
                    editorKey: getMainEditorKey({ element }),
                    editorId: elementType && isTaskList(elementType) ? getMainEditorId({ element }) : null,
                }),
            );
        },
        createShortcut: () => dispatch(createAliasToElement({ elementId: getElementId(selectedElements.first()) })),
        replaceImage: () =>
            filePicker?.open({
                accept: 'image/*',
                onSelect: ([file]) => {
                    if (file) uploadReplacementImage(file);
                },
            }),
        downloadOriginal,
        toggleImageAspectRatio: () => {
            const showFullAspectRatio = getShowFullAspectRatio(selectedElements.first());
            dispatch(
                updateMultipleElements({
                    updates: selectedElements.toArray().map((element) => ({
                        id: getElementId(element),
                        changes: { showFullAspectRatio: !showFullAspectRatio },
                    })),
                }),
            );
        },
        copyLinkToFile: () => {
            selectedElements.forEach((element) => {
                if (!element) return;

                const fileUrl = asObject<MNElement>(element).content?.file?.url;
                if (!fileUrl) return;

                const elementId = getElementId(element);
                const clipboardInsertSupported = !!(navigator.permissions && navigator.clipboard);

                if (clipboardInsertSupported) {
                    navigator.permissions.query(clipboardWritePermission).then((result) => {
                        if (result.state === 'granted' || result.state === 'prompt')
                            navigator.clipboard.writeText(fileUrl);
                    });
                } else {
                    dispatch(triggerDownload({ url: fileUrl, id: elementId } as Parameters<typeof triggerDownload>[0]));
                }
            });
        },
        copyLinkAddress: async () => {
            const clipboardInsertSupported =
                !!(navigator.permissions && navigator.clipboard) &&
                platformSingleton.browserEngine !== BrowserEngineType.firefox;
            if (!clipboardInsertSupported) return;

            const linkUrl = getLinkUrl(selectedElements.first());
            if (!linkUrl) return;
            if (isPlatformModernMobileApp(platformDetails)) return await writeToNativeClipboard(linkUrl);

            navigator.permissions
                .query(clipboardWritePermission)
                .then((result) => {
                    if (result.state === 'granted' || result.state === 'prompt') navigator.clipboard.writeText(linkUrl);
                })
                .catch(() => navigator.clipboard.writeText(linkUrl));
        },
        downloadSvg: () => downloadAsSvg({ element: selectedElements.first() }),
        groupIntoColumn,
        connectWithLines: () => dispatch(connectElementsWithLines(selectedElements) as unknown as UnknownAction),
        convertToSketch: () => dispatch(convertToSketch({ elementId: getElementId(selectedElements.first()) })),
        toggleLock: () => {
            const lockElements = !selectedElements.find(isElementLocked);
            dispatch(
                updateMultipleElements({
                    updates: selectedElements.toArray().map((element) => ({
                        id: getElementId(element),
                        changes: { locked: lockElements },
                    })),
                }),
            );
        },
        bringToFront: () => changeStackingOrder({ bringToFront: true }),
        sendToBack: () => changeStackingOrder({ bringToFront: false }),
        convertToTemplate: () => {
            const selectedBoardId = getElementId(selectedElements.filter(isBoard).first());
            if (selectedBoardId) dispatch(convertBoardToTemplate(selectedBoardId));
        },
        revertTemplateToBoard: () => {
            const selectedBoardId = getElementId(selectedElements.filter(isBoard).first());
            if (selectedBoardId) dispatch(revertTemplateToBoard(selectedBoardId));
        },
        convertToNote: () => {
            selectedElements.forEach((element) => {
                if (!isDocument(element)) return;

                dispatch(
                    setElementTypeAndUpdateElement({
                        id: getElementId(element),
                        elementType: ElementType.CARD_TYPE,
                        transactionId: getNewTransactionId(),
                    } as Parameters<typeof setElementTypeAndUpdateElement>[0]),
                );
            });
        },
        convertToDocument: () => dispatch(convertElementsToDocument(asObject(selectedElements.map(getElementId)))),
        convertColumnToBoard: () => {
            selectedElements.filter(isColumn).forEach((element) => {
                dispatch(convertColumnToBoard({ elementId: getElementId(element) }));
            });
        },
        logSelectedElement: (element) => console.log(element), // eslint-disable-line no-console
    };
};
